import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Settings,
  LogOut,
  Send,
  Loader2,
  Bot,
  User,
  Shield,
  Receipt,
  Sparkles,
  ChevronDown,
  Trash2,
  MoreVertical,
  CreditCard,
  Download,
  Menu,
  X,
  Sun,
  Moon,
  Share2,
  Link,
  Check,
  Mic,
  MicOff,
  Paperclip,
  Volume2,
  FileText as FileIcon,
  Image,
  XCircle,
  Bookmark,
  FileSearch,
  FileCheck,
  BookOpen,
  Zap,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Calendar,
  FileStack,
  BarChart3,
  AlertCircle,
  Clock,
  TrendingUp,
  Search,
  CheckCircle,
  Database,
  HelpCircle,
  Link2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { CitationCard } from '../components/ui/CitationCard';
import { CockpitGrid } from '../components/CockpitGrid';
import { NotificationBell } from '../components/NotificationBell';
import {
  createConversation,
  getConversations,
  getMessages,
  sendMessage,
  deleteConversation,
} from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';

import { BACKEND_URL } from '../config';

// Function to format markdown-like text to HTML
const formatMessage = (text) => {
  if (!text) return '';
  
  // First, extract and process tables separately
  const tableRegex = /\n?\|(.+)\|\n\|[\s:|-]+\|\n((?:\|.+\|\n?)+)/g;
  const tables = [];
  let textWithPlaceholders = text.replace(tableRegex, (match) => {
    const placeholder = `__TABLE_${tables.length}__`;
    tables.push(match.trim());
    return `\n${placeholder}\n`;
  });

  let formatted = textWithPlaceholders
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h4 class="text-base font-semibold text-primary-800 mt-4 mb-2">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="text-lg font-bold text-primary-900 mt-5 mb-3 pb-2 border-b border-slate-200">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="text-xl font-bold text-primary-900 mt-6 mb-4">$1</h2>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 text-primary-700 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800 underline underline-offset-2">$1</a>')
    // Lists with bullets
    .replace(/^- (.+)$/gm, '<li class="ml-4 pl-2 text-slate-700 relative before:content-[\"•\"] before:absolute before:-left-3 before:text-primary-500 before:font-bold">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 pl-2 text-slate-700 list-decimal">$2</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-4">')
    .replace(/\n/g, '<br/>');
  
  // Wrap in paragraph
  formatted = '<p class="mb-4">' + formatted + '</p>';
  
  // Fix list items by wrapping in ul
  formatted = formatted.replace(/(<li[^>]*>.*?<\/li>)+/g, '<ul class="space-y-2 my-3">$&</ul>');

  // Now replace table placeholders with rendered HTML tables
  tables.forEach((tableStr, idx) => {
    const rows = tableStr.split('\n').filter(r => r.trim().startsWith('|'));
    if (rows.length < 2) return;
    
    const parseRow = (row) => row.split('|').slice(1, -1).map(c => c.trim());
    const headers = parseRow(rows[0]);
    // Skip separator row (row[1])
    const dataRows = rows.slice(2).map(parseRow);
    
    let tableHtml = '<div class="overflow-x-auto my-4 rounded-lg border border-slate-200"><table class="w-full text-sm">';
    tableHtml += '<thead><tr class="bg-primary-50 border-b border-slate-200">';
    headers.forEach(h => {
      tableHtml += `<th class="px-4 py-2.5 text-left font-semibold text-primary-900 whitespace-nowrap">${h.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</th>`;
    });
    tableHtml += '</tr></thead><tbody>';
    dataRows.forEach((row, rIdx) => {
      const bgClass = rIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
      tableHtml += `<tr class="${bgClass} border-b border-slate-100">`;
      row.forEach(cell => {
        tableHtml += `<td class="px-4 py-2 text-slate-700">${cell.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')}</td>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody></table></div>';
    
    formatted = formatted.replace(`__TABLE_${idx}__`, tableHtml);
  });
  
  return formatted;
};

export default function Dashboard() {
  const { user, logoutUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-5.2');
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [availableModels, setAvailableModels] = useState({
    openai: [],
    anthropic: [],
    gemini: [],
  });
  const messagesEndRef = useRef(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showReminder, setShowReminder] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('kai_sidebar_collapsed') === 'true');
  const [modulesCollapsed, setModulesCollapsed] = useState(() => localStorage.getItem('kai_modules_collapsed') === 'true');
  const [attachedFile, setAttachedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceMode, setVoiceMode] = useState(() => localStorage.getItem('kai_voice_mode') === 'true');
  const currentAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [questionTemplates, setQuestionTemplates] = useState([]);
  const [dailyQuestion, setDailyQuestion] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('Ogolne');
  const [correctionModal, setCorrectionModal] = useState(null);
  const [correctionText, setCorrectionText] = useState('');

  const TAX_CATEGORIES = [
    { value: 'Ogolne', label: 'Ogolne', icon: HelpCircle },
    { value: 'VAT', label: 'VAT', icon: Receipt },
    { value: 'PIT', label: 'PIT', icon: FileIcon },
    { value: 'CIT', label: 'CIT', icon: BarChart3 },
    { value: 'ZUS', label: 'ZUS', icon: Shield },
    { value: 'KSeF', label: 'KSeF', icon: FileStack },
    { value: 'Rachunkowosc', label: 'Rachunkowosc', icon: Database },
  ];

  useEffect(() => {
    loadConversations();
    loadAvailableModels();
    checkSubscription();
    loadUserAvatar();
    loadQuestionTemplates();
    loadDailyQuestion();
    loadDashboardSummary();
    checkOnboarding();
  }, []);

  const loadQuestionTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/question-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestionTemplates(response.data);
    } catch {}
  };

  const loadDailyQuestion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/daily-question`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyQuestion(response.data);
    } catch {}
  };

  const loadDashboardSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/dashboard-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardSummary(response.data);
    } catch {}
  };

  const checkOnboarding = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data.completed) {
        setShowOnboarding(true);
      }
    } catch {}
  };

  const completeOnboarding = async () => {
    setShowOnboarding(false);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/onboarding-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch {}
  };

  const loadUserAvatar = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.avatar) {
        setUserAvatar(response.data.avatar);
      }
    } catch (error) {
      // Avatar not set, use default
    }
  };

  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation.id);
    }
  }, [activeConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
      
      if (response.data.is_blocked) {
        toast.error('Twoja subskrypcja wygasła. Odnów ją, aby kontynuować.');
        navigate('/subscription');
      } else if (response.data.show_reminder && response.data.reminder_message) {
        setShowReminder(true);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const response = await getConversations();
      setConversations(response.data);
    } catch (error) {
      toast.error('Nie udało się załadować konwersacji');
    }
  };

  const loadAvailableModels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableModels(response.data.models);
    } catch (error) {
      console.error('Error loading models:', error);
      // Fallback to default models if API fails
      setAvailableModels({
        openai: [
          { value: 'gpt-5.2', label: 'GPT-5.2 (Najnowszy)' },
          { value: 'gpt-4o', label: 'GPT-4o' },
        ],
        anthropic: [
          { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
        ],
        gemini: [
          { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash' },
        ],
      });
    }
  };

  const loadMessages = async (conversationId) => {
    try {
      const response = await getMessages(conversationId);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Nie udało się załadować wiadomości');
    }
  };

  const handleNewConversation = async () => {
    try {
      const response = await createConversation({
        title: 'Nowa rozmowa z KAI',
        model_used: selectedModel,
        provider: selectedProvider,
      });
      setConversations([response.data, ...conversations]);
      setActiveConversation(response.data);
      setMessages([]);
      toast.success('Nowa rozmowa utworzona');
    } catch (error) {
      toast.error('Nie udało się utworzyć rozmowy');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    // If file attached, use file endpoint
    if (attachedFile) {
      return handleSendWithFile(e);
    }
    if (!inputMessage.trim() || !activeConversation) return;

    const messageContent = inputMessage.trim();
    const userMessage = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await sendMessage({
        conversation_id: activeConversation.id,
        content: messageContent,
        model: selectedModel,
        provider: selectedProvider,
        category: selectedCategory,
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.content,
        citations: response.data.citations || [],
        timestamp: response.data.timestamp,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Auto-speak response if voice mode is on
      if (voiceMode && response.data.content) {
        speakResponse(response.data.content);
      }

      // Update conversation title if first message - will be auto-generated
      if (messages.length === 0) {
        // Backend generates topic async, refresh after delay
        setTimeout(refreshConversationTopics, 3000);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Osiągnięto limit zapytań. Spróbuj ponownie później.');
      } else if (error.response?.status === 402) {
        toast.error('Twoja subskrypcja wygasła. Odnów, aby kontynuować.');
        navigate('/subscription');
      } else {
        toast.error('Nie udało się wysłać wiadomości');
        console.error('Send message error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCorrection = async () => {
    if (!correctionModal || !correctionText.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/messages/${correctionModal.id}/correction`, {
        correction_text: correctionText,
        correction_type: 'factual'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Korekta zapisana. Dziekujemy!');
      setCorrectionModal(null);
      setCorrectionText('');
    } catch {
      toast.error('Blad zapisywania korekty');
    }
  };


  const handleDeleteConversation = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm('Czy na pewno chcesz usunąć tę rozmowę?')) return;
    
    try {
      await deleteConversation(convId);
      setConversations(conversations.filter(c => c.id !== convId));
      if (activeConversation?.id === convId) {
        setActiveConversation(null);
        setMessages([]);
      }
      toast.success('Rozmowa usunięta');
    } catch (error) {
      toast.error('Nie udało się usunąć rozmowy');
    }
  };

  // Voice mode toggle
  const toggleVoiceMode = () => {
    const newVal = !voiceMode;
    setVoiceMode(newVal);
    localStorage.setItem('kai_voice_mode', String(newVal));
    toast.success(newVal ? 'Tryb głosowy włączony' : 'Tryb głosowy wyłączony');
    if (!newVal && currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsSpeaking(false);
    }
  };

  // Voice recording - push-to-talk
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      toast.error('Nie można uzyskać dostępu do mikrofonu');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/voice/transcribe`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      
      const transcribedText = response.data.text;
      if (transcribedText) {
        setInputMessage(prev => prev ? `${prev} ${transcribedText}` : transcribedText);
        toast.success('Transkrypcja gotowa');
      }
    } catch (error) {
      toast.error('Nie udało się transkrybować audio');
    } finally {
      setLoading(false);
    }
  };

  const speakResponse = async (text) => {
    // Stop current audio if playing
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    if (isSpeaking) {
      setIsSpeaking(false);
      return;
    }
    setIsSpeaking(true);
    try {
      const token = localStorage.getItem('token');
      const selectedVoice = localStorage.getItem('kai_voice') || 'onyx';
      const response = await axios.post(
        `${BACKEND_URL}/api/voice/synthesize`,
        { text, voice: selectedVoice },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      audio.onended = () => {
        setIsSpeaking(false);
        currentAudioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        currentAudioRef.current = null;
      };
      await audio.play();
    } catch (error) {
      toast.error('Nie udało się odtworzyć odpowiedzi głosowej');
      setIsSpeaking(false);
    }
  };

  // File attachment
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Plik jest za duży (max 10MB)');
      return;
    }
    setAttachedFile(file);
  };

  const handleSendWithFile = async (e) => {
    e.preventDefault();
    if ((!inputMessage.trim() && !attachedFile) || !activeConversation) return;

    const messageContent = inputMessage.trim();
    const file = attachedFile;
    
    const userMessage = {
      role: 'user',
      content: messageContent + (file ? `\n[Załącznik: ${file.name}]` : ''),
      timestamp: new Date().toISOString(),
      file_info: file ? { filename: file.name, size: file.size, type: file.name.split('.').pop() } : null,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setAttachedFile(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('conversation_id', activeConversation.id);
      formData.append('content', messageContent);
      formData.append('provider', selectedProvider);
      formData.append('model', selectedModel);
      if (file) formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/chat/with-file`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      const assistantMessage = {
        role: 'assistant',
        content: response.data.content,
        citations: response.data.citations || [],
        timestamp: response.data.timestamp,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Auto-speak response if voice mode is on
      if (voiceMode && response.data.content) {
        speakResponse(response.data.content);
      }

      if (messages.length === 0) {
        setTimeout(refreshConversationTopics, 3000);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Osiągnięto limit zapytań.');
      } else if (error.response?.status === 402) {
        toast.error('Subskrypcja wygasła.');
        navigate('/subscription');
      } else {
        toast.error('Nie udało się wysłać wiadomości');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportConversation = async () => {
    if (!activeConversation || messages.length === 0) {
      toast.error('Brak wiadomości do eksportu');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/conversations/${activeConversation.id}/export/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `KAI_rozmowa_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Rozmowa wyeksportowana do PDF');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Nie udało się wyeksportować rozmowy');
    }
  };

  const handleShareConversation = async () => {
    if (!activeConversation || messages.length === 0) {
      toast.error('Brak wiadomości do udostępnienia');
      return;
    }

    setShareLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/conversations/${activeConversation.id}/share`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fullUrl = `${window.location.origin}/share/${response.data.share_id}`;
      setShareUrl(fullUrl);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Link skopiowany do schowka!');
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Nie udało się udostępnić rozmowy');
    } finally {
      setShareLoading(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    navigate('/');
  };

  const toggleSidebarCollapse = () => {
    const newVal = !sidebarCollapsed;
    setSidebarCollapsed(newVal);
    localStorage.setItem('kai_sidebar_collapsed', String(newVal));
  };

  const toggleModulesCollapse = () => {
    const newVal = !modulesCollapsed;
    setModulesCollapsed(newVal);
    localStorage.setItem('kai_modules_collapsed', String(newVal));
  };

  // Refresh conversations list to get updated topics
  const refreshConversationTopics = async () => {
    try {
      const response = await getConversations();
      setConversations(response.data);
    } catch {}
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Collapsible */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-50
        ${sidebarCollapsed ? 'w-16' : 'w-72'} sidebar-swiss flex flex-col shadow-lg
        transform transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo & User */}
        <div className="p-3 lg:p-4 border-b border-white/10">
          <div className="flex items-center justify-end mb-3">
            {/* Close button - mobile only */}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 text-white/70 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 bg-white/10 rounded-md p-2">
              <div className="w-8 h-8 rounded-md bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                {userAvatar ? (
                  <img src={userAvatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-300 truncate">{user?.email}</p>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="flex justify-center">
              <div className="w-8 h-8 rounded-md bg-white/20 flex items-center justify-center overflow-hidden">
                {userAvatar ? (
                  <img src={userAvatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Conversations */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="p-2 lg:p-3">
            <Button
              data-testid="new-conversation-button"
              onClick={() => {
                handleNewConversation();
                setSidebarOpen(false);
              }}
              className={`w-full btn-cta-swiss h-9 ${sidebarCollapsed ? 'px-2' : ''}`}
              title="Nowa Rozmowa"
            >
              <MessageSquare className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="ml-2">Nowa Rozmowa</span>}
            </Button>
          </div>

          {!sidebarCollapsed && (
            <ScrollArea className="flex-1 px-2 lg:px-3">
              <div className="space-y-1 pb-4">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    data-testid={`conversation-${conv.id}`}
                    onClick={() => {
                      setActiveConversation(conv);
                      setSidebarOpen(false);
                    }}
                    className={`group w-full text-left p-2 rounded-md text-sm transition-all cursor-pointer flex items-center justify-between ${
                      activeConversation?.id === conv.id
                        ? 'bg-white/15 border-l-2 border-[#f5a623]'
                        : 'hover:bg-white/10 border-l-2 border-transparent'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate text-sm">{conv.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(conv.updated_at).toLocaleDateString('pl-PL')}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteConversation(e, conv.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-300 transition-all"
                      title="Usuń rozmowę"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Bottom Menu - Collapsible Modules */}
        <div className={`border-t border-white/10 ${sidebarCollapsed ? 'p-1' : 'p-2'}`}>
          {/* Modules toggle */}
          <Button
            data-testid="modules-collapse-toggle"
            onClick={toggleModulesCollapse}
            variant="ghost"
            className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-between'} text-slate-300 hover:text-white hover:bg-white/10 h-8 mb-1`}
            title={modulesCollapsed ? 'Rozwiń moduły' : 'Zwiń moduły'}
          >
            <span className="flex items-center gap-2">
              <Menu className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-medium">Moduły</span>}
            </span>
            {!sidebarCollapsed && (
              <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${modulesCollapsed ? '-rotate-90' : ''}`} />
            )}
          </Button>

          {/* Module links - collapsible */}
          <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
            modulesCollapsed && !sidebarCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'
          }`}>
            <div className="space-y-0.5">
              {[
                { id: 'nav-subscription', icon: CreditCard, label: 'Subskrypcja', path: '/subscription', cls: 'text-green-400 hover:text-green-300 hover:bg-green-900/20' },
                { id: 'nav-invoices', icon: Receipt, label: 'Faktury', path: '/invoices', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-calculator', icon: Sparkles, label: 'Kalkulator', path: '/calculator', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-law-changes', icon: Shield, label: 'Zmiany przepisów', path: '/law-changes', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-favorites', icon: Bookmark, label: 'Ulubione', path: '/favorites', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-review', icon: FileSearch, label: 'Sprawdź dokument', path: '/review', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-invoice-auditor', icon: FileCheck, label: 'Audytor faktur', path: '/invoice-auditor', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-translator', icon: BookOpen, label: 'Tłumacz przepisów', path: '/regulation-translator', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-specialist-bots', icon: Bot, label: 'Specjaliści AI', path: '/specialist-bots', cls: 'text-cyan-300 hover:text-cyan-200 hover:bg-cyan-900/20' },
                { id: 'nav-import-data', icon: Database, label: 'Import danych', path: '/import-data', cls: 'text-teal-300 hover:text-teal-200 hover:bg-teal-900/20' },
                { id: 'nav-ksef', icon: Zap, label: 'KSeF', path: '/ksef', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-templates', icon: FileStack, label: 'Szablony', path: '/templates', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-tax-compare', icon: BarChart3, label: 'Porównywarka', path: '/tax-comparison', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-jpk', icon: Search, label: 'Analiza JPK', path: '/jpk', cls: 'text-slate-300 hover:text-white hover:bg-white/10' },
                { id: 'nav-integrations', icon: Link2, label: 'Integracje', path: '/integrations', cls: 'text-orange-300 hover:text-orange-200 hover:bg-orange-900/20' },
              ].map(item => (
                <Button
                  key={item.id}
                  data-testid={item.id}
                  onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                  variant="ghost"
                  className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} ${item.cls} h-8`}
                  title={item.label}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="text-xs">{item.label}</span>}
                </Button>
              ))}
            </div>
          </div>

          {/* Admin Panel - always visible for admins */}
          {user?.role === 'admin' && (
            <div className={`mt-1 pt-1 border-t border-white/5 ${sidebarCollapsed ? 'px-1' : 'px-2'}`}>
              <Button
                data-testid="admin-panel-button"
                onClick={() => { navigate('/admin'); setSidebarOpen(false); }}
                variant="ghost"
                className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 h-8`}
                title="Panel Admina"
              >
                <Shield className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span className="text-xs font-medium">Panel Admina</span>}
              </Button>
            </div>
          )}

          {/* Always visible: settings + guide + sidebar collapse + theme + logout */}
          <div className={`mt-1 pt-1 border-t border-white/5 space-y-0.5`}>
            <Button
              data-testid="nav-settings"
              onClick={() => { navigate('/settings'); setSidebarOpen(false); }}
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} text-slate-300 hover:text-white hover:bg-white/10 h-8`}
              title="Ustawienia"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-medium">Ustawienia</span>}
            </Button>
            <Button
              data-testid="nav-guide"
              onClick={() => { navigate('/guide'); setSidebarOpen(false); }}
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} text-orange-400 hover:text-orange-300 hover:bg-orange-900/20 h-8`}
              title="Instrukcja obsługi"
            >
              <HelpCircle className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs font-medium">Instrukcja</span>}
            </Button>
            <Button
              data-testid="sidebar-collapse-toggle"
              onClick={toggleSidebarCollapse}
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} text-slate-400 hover:text-white hover:bg-white/10 h-8`}
              title={sidebarCollapsed ? 'Rozwiń pasek' : 'Zwiń pasek'}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4 flex-shrink-0" /> : <PanelLeftClose className="w-4 h-4 flex-shrink-0" />}
              {!sidebarCollapsed && <span className="text-xs">{sidebarCollapsed ? 'Rozwiń' : 'Zwiń pasek'}</span>}
            </Button>
            <Button
              data-testid="theme-toggle-button"
              onClick={toggleTheme}
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 h-8`}
              title={isDark ? 'Tryb jasny' : 'Tryb ciemny'}
            >
              {isDark ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
              {!sidebarCollapsed && <span className="text-xs">{isDark ? 'Tryb jasny' : 'Tryb ciemny'}</span>}
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-1' : 'justify-start gap-3'} text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8`}
              title="Wyloguj"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && <span className="text-xs">Wyloguj</span>}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-[#F8FAFC] dark:bg-slate-900 min-w-0">
        {/* Top Bar - Swiss Style */}
        <div className="header-swiss p-3 lg:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Hamburger menu - mobile only */}
              <button
                data-testid="mobile-menu-button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="rounded-2xl flex items-center justify-center overflow-hidden" style={{ width: '7.5rem', height: '7.5rem', boxShadow: '4px 6px 12px rgba(0,0,0,0.35), -2px -2px 6px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.15)', transform: 'perspective(400px) rotateY(-3deg)' }}>
                <img src="/kai-logo.png" alt="KAI" className="w-full h-full object-cover rounded-2xl" />
              </div>
              <div className="hidden sm:block">
                <h2 className="font-heading text-2xl lg:text-3xl text-[#1e3a5f] dark:text-slate-100 font-bold tracking-tight">KAI</h2>
                <p className="text-sm lg:text-base text-slate-500 dark:text-slate-400">Asystent Księgowy AI</p>
              </div>
            </div>

            <div className="flex items-center gap-1 lg:gap-2">
              <NotificationBell />
              {activeConversation && messages.length > 0 && (
                <Button
                  data-testid="export-conversation-button"
                  onClick={handleExportConversation}
                  variant="outline"
                  className="h-8 lg:h-9 px-2 lg:px-3 text-xs lg:text-sm border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 dark:text-slate-200"
                  title="Eksportuj rozmowę do PDF"
                >
                  <Download className="w-4 h-4 lg:mr-1" />
                  <span className="hidden lg:inline">PDF</span>
                </Button>
              )}
              {activeConversation && messages.length > 0 && (
                <Button
                  data-testid="share-conversation-button"
                  onClick={handleShareConversation}
                  disabled={shareLoading}
                  variant="outline"
                  className={`h-8 lg:h-9 px-2 lg:px-3 text-xs lg:text-sm border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 dark:text-slate-200 ${shareUrl ? 'text-green-600 dark:text-green-400 border-green-300 dark:border-green-600' : ''}`}
                  title="Udostępnij rozmowę"
                >
                  {shareLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : shareUrl ? (
                    <>
                      <Check className="w-4 h-4 lg:mr-1" />
                      <span className="hidden lg:inline">Skopiowano!</span>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-4 h-4 lg:mr-1" />
                      <span className="hidden lg:inline">Udostępnij</span>
                    </>
                  )}
                </Button>
              )}
              {/* Model selectors - hidden on very small screens */}
              <div className="hidden sm:flex items-center gap-1 lg:gap-2">
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger data-testid="provider-select" className="w-20 lg:w-28 h-8 lg:h-9 text-xs lg:text-sm input-swiss">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="anthropic">Claude</SelectItem>
                    <SelectItem value="gemini">Gemini</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger data-testid="model-select" className="w-28 lg:w-44 h-8 lg:h-9 text-xs lg:text-sm input-swiss">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels[selectedProvider]?.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Subscription Reminder Banner */}
          {showReminder && subscriptionStatus?.reminder_message && (
            <div className="max-w-4xl mx-auto mt-2 lg:mt-3 p-2 lg:p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0">
                  <div className="w-7 lg:w-8 h-7 lg:h-8 rounded-md bg-amber-500 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-3 lg:w-4 h-3 lg:h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-amber-800 text-xs lg:text-sm">Przypomnienie</p>
                    <p className="text-xs lg:text-sm text-amber-700 truncate">{subscriptionStatus.reminder_message}</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-amber-500 hover:bg-amber-600 text-white h-8 lg:h-10 px-3 lg:px-4 text-xs lg:text-sm flex-shrink-0"
                >
                  Przedłuż
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-3 lg:p-6">
          <div className="max-w-5xl mx-auto space-y-4 lg:space-y-6">
            {messages.length === 0 && activeConversation && (
              <div className="text-center py-8 lg:py-12" data-testid="empty-chat-state">
                <div className="w-36 lg:w-48 h-36 lg:h-48 rounded-full bg-white shadow-xl flex items-center justify-center mx-auto mb-4 lg:mb-6 overflow-hidden border-4 border-primary-100 dark:border-slate-600">
                  <img src="/images/kai_avatar.jpeg" alt="KAI" className="w-full h-full object-cover" style={{ objectPosition: 'center 10%' }} />
                </div>
                <h3 className="font-heading text-xl lg:text-2xl text-primary-900 dark:text-slate-100 mb-2">
                  Witaj! Jestem KAI
                </h3>
                <p className="text-muted-foreground dark:text-slate-400 text-sm mb-6">
                  Zadaj mi pytanie lub wybierz gotowy temat:
                </p>
                {/* Question Templates */}
                <div className="max-w-2xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-2 px-4">
                  {questionTemplates.slice(0, 4).flatMap(cat =>
                    cat.questions.slice(0, 1).map((q, i) => (
                      <button
                        key={`${cat.category}-${i}`}
                        data-testid={`template-${cat.category}`}
                        onClick={() => { setInputMessage(q); }}
                        className="text-left text-sm p-3 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
                      >
                        <span className="text-xs font-medium text-primary-500 dark:text-primary-400">{cat.category}</span>
                        <p className="mt-0.5">{q}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {!activeConversation && messages.length === 0 && (
              <CockpitGrid
                user={user}
                dashboardSummary={dashboardSummary}
                dailyQuestion={dailyQuestion}
                onNewConversation={handleNewConversation}
                onSetInput={setInputMessage}
              />
            )}

            {messages.map((message, idx) => (
              <div
                key={idx}
                data-testid={`message-${message.role}-${idx}`}
                className={`flex gap-2 lg:gap-3 animate-fade-in ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="w-[4.5rem] lg:w-20 h-[4.5rem] lg:h-20 rounded-lg overflow-hidden flex-shrink-0 shadow-sm bg-white">
                    <img src="/images/kai_avatar.jpeg" alt="KAI" className="w-full h-full object-cover" style={{ objectPosition: 'center 10%' }} />
                  </div>
                )}
                <div
                  className={`max-w-[85%] lg:max-w-2xl ${
                    message.role === 'user'
                      ? 'chat-bubble-user'
                      : 'chat-bubble-ai'
                  }`}
                >
                  {message.role === 'user' ? (
                    <div>
                      <p className="text-xs lg:text-sm leading-relaxed">
                        {message.content}
                      </p>
                      {message.file_info && (
                        <div className="mt-1.5 flex items-center gap-1.5 text-xs opacity-80">
                          {message.file_info.type && ['jpg','jpeg','png','webp','gif'].includes(message.file_info.type) ? (
                            <Image className="w-3 h-3" />
                          ) : (
                            <FileIcon className="w-3 h-3" />
                          )}
                          <span>{message.file_info.filename}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="prose prose-slate dark:prose-invert prose-sm max-w-none">
                      <div 
                        className="text-xs lg:text-sm leading-relaxed text-slate-700 dark:text-slate-200"
                        dangerouslySetInnerHTML={{ 
                          __html: formatMessage(message.content) 
                        }}
                      />
                    </div>
                  )}
                  {message.citations && message.citations.length > 0 && (
                    <div className="mt-3 lg:mt-4 pt-2 lg:pt-3 border-t border-slate-100 dark:border-slate-600 space-y-1.5">
                      <p className="text-xs font-semibold text-[#1e3a5f] dark:text-[#5b9ad8] mb-2 flex items-center gap-2">
                        <span className="w-1 h-4 bg-[#f5a623] rounded-full"></span>
                        Źródła
                      </p>
                      {message.citations.map((citation, citIdx) => (
                        <CitationCard key={citIdx} citation={citation} />
                      ))}
                    </div>
                  )}
                  {message.role === 'assistant' && (
                    <div className="mt-2 flex items-center gap-1 flex-wrap">
                      {/* Confidence score badge — aggregate quality signal */}
                      {message.confidence && typeof message.confidence.score === 'number' && (
                        <span
                          data-testid={`confidence-badge-${idx}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mr-1 ${
                            message.confidence.color === 'green'
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
                              : message.confidence.color === 'amber'
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                              : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-300 dark:border-rose-700'
                          }`}
                          title={`Pewność odpowiedzi: ${message.confidence.score}/100 (${message.confidence.label})
KB: ${message.confidence.components?.kb_coverage || 0}/25
Wiarygodność źródeł: ${message.confidence.components?.source_credibility || 0}/20
Fact-check: ${message.confidence.components?.fact_check || 0}/25
Weryfikacja krzyżowa: ${message.confidence.components?.cross_verification || 0}/10`}
                        >
                          <Shield className="w-2.5 h-2.5" />
                          Pewność: {message.confidence.score}%
                        </span>
                      )}
                      {/* Verification badge */}
                      {message.verification && (
                        <span
                          data-testid={`verification-badge-${idx}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium mr-1 ${
                            message.verification?.result?.is_correct === false
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : message.verification?.status === 'verified_multi'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : message.verification?.status === 'verified_kb'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : message.verification?.status === 'verified_web'
                              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400'
                              : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                          }`}
                          title={
                            message.verification?.result?.is_correct === false
                              ? `Problem: ${(message.verification.result.issues || []).join(', ')}`
                              : message.verification?.sources_used?.join(', ') || 'Weryfikacja'
                          }
                        >
                          {message.verification?.result?.is_correct === false ? (
                            <><X className="w-2.5 h-2.5" /> Wymaga korekty</>
                          ) : message.verification?.status === 'verified_multi' ? (
                            <><Check className="w-2.5 h-2.5" /> Baza + Internet</>
                          ) : message.verification?.status === 'verified_kb' ? (
                            <><Check className="w-2.5 h-2.5" /> Baza wiedzy</>
                          ) : message.verification?.status === 'verified_web' ? (
                            <><Check className="w-2.5 h-2.5" /> Internet</>
                          ) : (
                            <><Shield className="w-2.5 h-2.5" /> AI</>
                          )}
                        </span>
                      )}
                      {message.is_correction && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 mr-1">
                          Autokorekta
                        </span>
                      )}
                      <button
                        data-testid={`speak-btn-${idx}`}
                        onClick={() => speakResponse(message.content)}
                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        title={isSpeaking ? 'Zatrzymaj' : 'Odczytaj głosowo'}
                      >
                        {isSpeaking ? (
                          <X className="w-3.5 h-3.5 text-red-500" />
                        ) : (
                          <Volume2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button
                        data-testid={`fav-btn-${idx}`}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const prevMsg = messages[idx - 1];
                            await axios.post(`${BACKEND_URL}/api/favorites`, {
                              question: prevMsg?.role === 'user' ? prevMsg.content : '',
                              answer: message.content,
                              conversation_id: activeConversation?.id || ''
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success('Zapisano w ulubionych');
                          } catch { toast.error('Błąd zapisywania'); }
                        }}
                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-yellow-500 transition-colors"
                        title="Zapisz w ulubionych"
                      >
                        <Bookmark className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`export-word-${idx}`}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const prevMsg = messages[idx - 1];
                            const resp = await axios.post(`${BACKEND_URL}/api/export/word`, {
                              content: message.content,
                              title: prevMsg?.role === 'user' ? prevMsg.content.substring(0, 60) : 'Analiza KAI',
                              company_name: user?.company_name || '',
                              company_nip: user?.nip || ''
                            }, {
                              headers: { Authorization: `Bearer ${token}` },
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([resp.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `KAI_${new Date().toISOString().split('T')[0]}.pdf`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                            toast.success('PDF pobrany');
                          } catch { toast.error('Błąd eksportu'); }
                        }}
                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-blue-500 transition-colors"
                        title="Eksportuj do PDF"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`correct-btn-${idx}`}
                        onClick={() => {
                          setCorrectionModal(message);
                          setCorrectionText('');
                        }}
                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-orange-500 transition-colors"
                        title="Popraw odpowiedz"
                      >
                        <AlertCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="w-16 lg:w-[4.5rem] h-16 lg:h-[4.5rem] rounded-md overflow-hidden flex-shrink-0 shadow-sm bg-slate-200 dark:bg-slate-600 flex items-center justify-center">
                    {userAvatar ? (
                      <img src={userAvatar} alt="Ty" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-slate-500 dark:text-slate-300" />
                    )}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-2 lg:gap-3 justify-start animate-fade-in" data-testid="loading-indicator">
                <div className="w-[4.5rem] lg:w-20 h-[4.5rem] lg:h-20 rounded-lg overflow-hidden flex-shrink-0 shadow-sm bg-white">
                  <img src="/images/kai_avatar.jpeg" alt="KAI" className="w-full h-full object-cover" style={{ objectPosition: 'center 10%' }} />
                </div>
                <div className="chat-bubble-ai">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-[#1e3a5f] dark:bg-[#5b9ad8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-[#1e3a5f] dark:bg-[#5b9ad8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-[#1e3a5f] dark:bg-[#5b9ad8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                    <span className="text-sm text-slate-500 dark:text-slate-400">KAI pisze...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input - Swiss Style */}
        {activeConversation && (
          <div className="bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-2 lg:p-4">
            {/* Speaking indicator with stop button */}
            {isSpeaking && (
              <div className="max-w-4xl mx-auto mb-2">
                <div className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                    <Volume2 className="w-4 h-4 animate-pulse" />
                    <span>KAI mówi...</span>
                  </div>
                  <Button
                    data-testid="stop-speaking-btn"
                    variant="ghost"
                    size="sm"
                    onClick={() => speakResponse('')}
                    className="h-7 px-3 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Stop
                  </Button>
                </div>
              </div>
            )}
            {/* Attached file preview */}
            {attachedFile && (
              <div className="max-w-4xl mx-auto mb-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg text-sm">
                  {attachedFile.type?.startsWith('image/') ? (
                    <Image className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <FileIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  )}
                  <span className="flex-1 truncate text-blue-800 dark:text-blue-300">{attachedFile.name}</span>
                  <span className="text-blue-500 dark:text-blue-400 text-xs">({(attachedFile.size / 1024).toFixed(0)} KB)</span>
                  <button
                    onClick={() => setAttachedFile(null)}
                    className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded"
                    data-testid="remove-attachment-btn"
                  >
                    <XCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
              {/* Category selector */}
              <div className="flex items-center gap-1 mb-2 overflow-x-auto pb-1" data-testid="category-selector">
                {TAX_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      data-testid={`category-${cat.value}`}
                      onClick={() => setSelectedCategory(cat.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                        selectedCategory === cat.value
                          ? 'bg-[#1e3a5f] text-white shadow-sm'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2 items-end">
                {/* File attachment button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf,.txt,.csv,.md,.xlsx,.xls"
                  onChange={handleFileSelect}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="attach-file-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="h-10 lg:h-11 px-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  title="Załącz plik lub zdjęcie"
                >
                  <Paperclip className="w-5 h-5" />
                </Button>

                {/* Voice mode toggle */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="voice-mode-toggle"
                  onClick={toggleVoiceMode}
                  className={`h-10 lg:h-11 px-2 ${
                    voiceMode 
                      ? 'text-green-500 hover:text-green-700 bg-green-50 dark:bg-green-900/20' 
                      : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300'
                  }`}
                  title={voiceMode ? 'Tryb głosowy włączony' : 'Tryb głosowy wyłączony'}
                >
                  <Volume2 className="w-5 h-5" />
                </Button>

                {/* Voice recording button - push to talk */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="voice-record-btn"
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onMouseLeave={() => { if (isRecording) stopRecording(); }}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  disabled={loading && !isRecording}
                  className={`h-10 lg:h-11 px-2 ${
                    isRecording 
                      ? 'text-red-500 hover:text-red-700 bg-red-50 dark:bg-red-900/30 animate-pulse' 
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                  title="Trzymaj aby mówić"
                >
                  {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>

                <textarea
                  data-testid="message-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if ((inputMessage.trim() || attachedFile) && !loading) {
                        handleSendMessage(e);
                      }
                    }
                  }}
                  placeholder={isRecording ? 'Mów teraz... (puść aby wysłać)' : 'Zadaj pytanie...'}
                  disabled={loading}
                  rows={1}
                  className="flex-1 min-h-[40px] lg:min-h-[44px] max-h-32 text-sm px-3 lg:px-4 py-2 lg:py-3 input-swiss resize-none"
                  style={{ height: 'auto' }}
                />
                <Button
                  data-testid="send-button"
                  type="submit"
                  disabled={loading || (!inputMessage.trim() && !attachedFile)}
                  className="h-10 lg:h-11 px-4 lg:px-6 btn-primary-swiss self-end"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" data-testid="onboarding-modal">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Steps */}
            {onboardingStep === 0 && (
              <div className="p-6 text-center">
                <div className="w-20 h-20 rounded-xl bg-[#1e3a5f] flex items-center justify-center mx-auto mb-4">
                  <img src="/kai-logo.png" alt="KAI" className="w-16 h-16 object-contain" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-2">Witaj w KAI!</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                  Twój inteligentny asystent księgowy. Pokażę Ci w 3 krokach jak ze mnie korzystać.
                </p>
                <Button onClick={() => setOnboardingStep(1)} className="bg-[#1e3a5f] hover:bg-[#2a4a70] text-white w-full h-11" data-testid="onboarding-next-0">
                  Zaczynamy!
                </Button>
              </div>
            )}
            {onboardingStep === 1 && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white">1. Wybierz kategorie</h3>
                    <p className="text-xs text-slate-500">Krok 1 z 3</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                  Wybierz rodzaj podatku, ktorego dotyczy pytanie. Dzieki temu KAI przeszuka <strong>najbardziej trafne zrodla</strong>.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['VAT', 'PIT', 'CIT', 'ZUS', 'KSeF', 'Rachunkowosc'].map(c => (
                    <span key={c} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">{c}</span>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mb-4">Kategoria "Ogolne" obejmuje pytania z kazdej dziedziny.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOnboardingStep(0)} className="flex-1">Wstecz</Button>
                  <Button onClick={() => setOnboardingStep(2)} className="flex-1 bg-[#1e3a5f] hover:bg-[#2a4a70] text-white" data-testid="onboarding-next-1">Dalej</Button>
                </div>
              </div>
            )}
            {onboardingStep === 2 && (
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white">2. Zadaj pytanie</h3>
                    <p className="text-xs text-slate-500">Krok 2 z 3</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                  KAI odpowiada na podstawie <strong>interpretacji podatkowych</strong>, <strong>wyrokow NSA/WSA</strong> i bazy wiedzy.
                </p>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 mb-4 space-y-1">
                  <p className="text-xs text-slate-500 italic">"Jak obliczyc skladke zdrowotna dla JDG w 2026?"</p>
                  <p className="text-xs text-slate-500 italic">"Jaka stawka ryczaltu dla uslug IT?"</p>
                  <p className="text-xs text-slate-500 italic">"Czy moge odliczyc VAT od samochodu osobowego?"</p>
                </div>
                <p className="text-xs text-slate-400 mb-4">Mozesz tez przeslac zdjecie faktury lub dokument do analizy.</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOnboardingStep(1)} className="flex-1">Wstecz</Button>
                  <Button onClick={() => setOnboardingStep(3)} className="flex-1 bg-[#1e3a5f] hover:bg-[#2a4a70] text-white" data-testid="onboarding-next-2">Dalej</Button>
                </div>
              </div>
            )}
            {onboardingStep === 3 && (
              <div className="p-6 text-center">
                <div className="w-16 h-16 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-2">3. Sprawdz zrodlo!</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                  KAI pokazuje podstawe prawna, nr interpretacji i sygnatury wyrokow. Mozesz tez <strong>poprawic odpowiedz</strong> jesli cos jest nie tak.
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                  Masz <strong>7 dni darmowego trialu</strong>. Kliknij "Subskrypcja" aby zobaczyc plany.
                </p>
                <Button onClick={completeOnboarding} className="bg-green-600 hover:bg-green-700 text-white w-full h-11" data-testid="onboarding-finish">
                  Rozpocznij prace z KAI
                </Button>
              </div>
            )}
            {/* Progress dots */}
            <div className="flex justify-center gap-2 pb-4">
              {[0, 1, 2, 3].map(s => (
                <div key={s} className={`w-2 h-2 rounded-full transition-all ${onboardingStep === s ? 'bg-[#1e3a5f] w-6' : 'bg-slate-300 dark:bg-slate-600'}`} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Correction Modal */}
      {correctionModal && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" data-testid="correction-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Popraw odpowiedz KAI</h3>
              <button onClick={() => setCorrectionModal(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
              Opisz co jest niepoprawne w odpowiedzi. Twoja korekta pomoze poprawic jakosc przyszlych odpowiedzi.
            </p>
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-4">{correctionModal.content?.substring(0, 300)}...</p>
            </div>
            <textarea
              data-testid="correction-input"
              value={correctionText}
              onChange={(e) => setCorrectionText(e.target.value)}
              placeholder="Opisz poprawke, np. 'Prawidlowa stawka to 14% na podstawie art. 12 ust. 1 pkt 2a...'"
              rows={4}
              className="w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
            />
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => setCorrectionModal(null)}
                variant="outline"
                className="flex-1"
              >
                Anuluj
              </Button>
              <Button
                data-testid="submit-correction-btn"
                onClick={handleSubmitCorrection}
                disabled={!correctionText.trim()}
                className="flex-1 bg-[#1e3a5f] hover:bg-[#2a4a70] text-white"
              >
                Wyslij korekta
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
